/**
 * Detecta módulos de páginas individuales/inline (urls con /new, /nuevo o /:parametro)
 * @param {string} url - URL del módulo
 * @returns {boolean}
 */
export const isInlineModuleUrl = (url) => {
    if (!url || typeof url !== 'string') return false
    const normalized = `/${url.replace(/^\/+|\/+$/g, '')}`.toLowerCase()
    return (
        normalized.endsWith('/new') ||
        normalized.endsWith('/nuevo') ||
        /\/:[^/]+$/.test(normalized)
    )
}

const HIDDEN_MODULE_URLS = ['mobile_routes']

/**
 * Detecta procesos ocultos en las superficies de navegación y configuración.
 * También cubre rutas individuales, por ejemplo /mobile_routes/123.
 * @param {Object} module - Módulo o ruta
 * @returns {boolean}
 */
export const isHiddenModule = (module) => {
    if (!module || typeof module !== 'object') return false

    const moduleUrl = module.url || module.to || module.path ||
        (typeof module.id === 'string' ? module.id : '')
    if (typeof moduleUrl !== 'string') return false

    const normalizedUrl = moduleUrl
        .split(/[?#]/, 1)[0]
        .replace(/^\/+|\/+$/g, '')
        .toLowerCase()

    return HIDDEN_MODULE_URLS.some((hiddenUrl) =>
        normalizedUrl === hiddenUrl || normalizedUrl.startsWith(`${hiddenUrl}/`)
    )
}

/**
 * @param {Array} modules - Lista de módulos del usuario
 * @returns {Array} - Módulos filtrados
 */
export const filterModulesForDisplay = (modules, user = null) => {
    if (!Array.isArray(modules)) return []

    return modules.filter((mod) => {
        const { permisitions, is_tab } = mod
        //exclusivo para grupo entero
        // Lista de subgrupos (carpetas dentro de grupos) a ocultar
        const hiddenSubGroups = [
            'Gestión de Viajes 2',
        ]
        if (hiddenSubGroups.includes(mod.sub_group)) return false

        // Ocultar Reportes de Viajes en Transporte de Personal
        if ((mod.module_group === 'Transporte de Personal' || mod.module_group === 'T. Personal') &&
            (mod.sub_group === 'Reportes de Viajes' || mod.sub_group === 'Reporte de Viajes')) {
            return false
        }

        // Exclusión por folder
        if (mod.folder === './mantenimientos/EmployeeStatePage') return false

        // Exclusiones por nombre exacto
        if (mod.name === 'Historial de cambios de odómetro') return false
        if (mod.name === 'Historial de cambios de odómetro de GPS') return false
        if (mod.name === 'Historial de cambios de odómetro remolque') return false
        if (mod.name === 'Historial de cambios de odómetro GPS de remolque') return false
        if (mod.name === 'Entrega de Mercancía') return false
        if (mod.name === 'Planificación de Viajes') return false
        if (mod.name === 'Planta de Clientes') return false
        if (mod.name === 'Categorías de Inspección') return false
        if (mod.name === 'Plantillas de Optimización de Rutas') return false
        if (mod.name === 'Coordinación') return false

        // Ocultar módulos de Modelado que fueron integrados en otros módulos
        if (mod.module_group === 'Modelado' && mod.name === 'Categorías de procesos') return false
        if (mod.module_group === 'Modelado' && mod.name === 'Catálogos Personalizados') return false

        // Ocultar "Categorías de prospectos" del sidebar (redundante, existe en otra sección)
        if (mod.url === 'prospect_category') return false

        // Ocultar módulos configurados para no mostrarse en el sistema
        if (isHiddenModule(mod)) return false

        // Excluir tabs
        if (is_tab) return false

        // DEBUG: Descomenta la siguiente línea para ver las propiedades del usuario en la consola y saber cuál usar (profile_name, role_name, etc.)
        // console.log('User object:', user)

        // Ejemplo: Ocultar proceso si el usuario pertenece a la tabla de usuarios (tiene propiedad username)
        if (user?.username) {
            // Agrega aquí los nombres EXACTOS de los procesos a ocultar
            if (mod.name === 'Pasajeros') return false
        }

        // Solo mostrar módulos con permiso ACCESS
        return permisitions?.ACCESS
    })
}

/**
 * 
 * @param {Array} modules - Lista de módulos del usuario
 * @param {Object} user - Objeto del usuario (opcional)
 * @param {Function} t - Función de traducción de i18next (useTranslation)
 * @returns {Array} - Objetos formateados para búsqueda {key, title, value, description}
 */
export const filterModulesForSearch = (modules, user = null, t = (v) => v) => {
    const filtered = filterModulesForDisplay(modules, user)

    return filtered
        .filter(m => m.isVisible) // Filtro adicional para búsqueda
        .map((m) => {
            return {
                key: m.id,
                title: t(m.module_group),
                value: m.url,
                description: t(m.name),
            }
        })
}
